<template>
  <!-- 请假统计：已通过的请假天数、假别构成与部门分布（审批中/已驳回不计入发生额） -->
  <div class="leave-stat-report">
    <list-page>
      <search-condition @search="loadReport" :show-search="true" page-name="leaveStatReport">
        <template #default>
          <el-select v-model="query.leaveTypeId" placeholder="全部" clearable>
            <template #prefix>请假类型</template>
            <el-option v-for="t in typeList" :key="t.id" :label="t.dictName" :value="t.id"/>
          </el-select>
          <depart-select v-model="query.departCode" :data="departTree" placeholder="全部部门"/>
          <!-- 按年月过滤：年月本身已含年份，不再单独放年度条件 -->
          <el-date-picker v-model="query.monthLabel" type="month" value-format="YYYY-MM"
                          placeholder="全部月份" clearable style="width: 160px"/>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadReport">查询</page-button>
        </template>
      </search-condition>
      <list-table>
        <!-- KPI -->
        <div class="stat-grid">
          <div class="stat-card">
            <div class="label">已通过天数</div>
            <div class="value">{{ days(report.passedDays) }}<span class="unit">天</span></div>
            <div class="sub">审批中 / 已驳回不计入</div>
          </div>
          <div class="stat-card">
            <div class="label">已通过单数</div>
            <div class="value">{{ report.passedCount || 0 }}<span class="unit">单</span></div>
          </div>
          <div class="stat-card">
            <div class="label">请假人数</div>
            <div class="value">{{ report.peopleCount || 0 }}<span class="unit">人</span></div>
          </div>
          <div class="stat-card">
            <div class="label">涉及部门</div>
            <div class="value">{{ report.departCount || 0 }}<span class="unit">个</span></div>
          </div>
        </div>
        <!-- 口径说明：只有已通过的假单才计入发生额，审批中/已驳回都不算 -->
        <div class="tip-bar">
          口径说明：只有「已通过」的请假单计入发生额，审批中 / 已驳回不计入；
          请假天数按起止时间推导，某一天上午 / 下午各计 0.5 天。
          下方「长期假单」指单张 ≥ 5 天的假单，需提前做岗位排班与工作交接。
        </div>

        <!-- 人员请假天数：按人统计，天数降序 -->
        <list-table v-loading="loading" background-color="#FFF" style="height: auto" class="table-list-margin">
          <yq-table :data="report.peopleRows || []" :empty-text="'暂无数据'" height="auto">
            <yq-table-column label="职员" prop="dimTitle" min-width="130rem">
              <template v-slot="{row}">{{ row.dimTitle || row.dimKey || "—" }}</template>
            </yq-table-column>
            <yq-table-column label="请假单数" prop="applyCount" align="right" width="110rem">
              <template v-slot="{row}"><span class="num">{{ row.applyCount || 0 }}</span></template>
            </yq-table-column>
            <yq-table-column label="请假天数" prop="totalDays" align="right" width="110rem">
              <template v-slot="{row}"><span class="num bold">{{ days(row.totalDays) }}</span></template>
            </yq-table-column>
            <yq-table-column label="单均天数" prop="avgDays" align="right" width="110rem">
              <template v-slot="{row}"><span class="num">{{ days(row.avgDays) }}</span></template>
            </yq-table-column>
            <yq-table-column label="天数占比" prop="daysRatio" min-width="170rem">
              <template v-slot="{row}">
                <div class="ratio-cell">
                  <span class="ratio-track">
                    <i :style="{width: (row.daysRatio || 0) + '%', background: '#13b8b8'}"/>
                  </span>
                  <span class="ratio-text">{{ row.daysRatio || 0 }}%</span>
                </div>
              </template>
            </yq-table-column>
          </yq-table>
        </list-table>

        <!-- 假别构成 + 部门排名 -->
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">假别构成（按天数）</div>
            <div ref="typeChart" class="chart-div"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">部门请假天数排名</div>
            <div ref="departChart" class="chart-div"></div>
          </div>
        </div>

        <!-- 人员排名 + 月度 -->
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">人员请假天数排名</div>
            <div ref="peopleChart" class="chart-div"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">月度请假天数</div>
            <div ref="monthChart" class="chart-div"></div>
          </div>
        </div>

        <!-- 假别统计明细 -->
        <list-table v-loading="loading" background-color="#FFF" style="height: auto" class="table-list-margin">
          <yq-table :data="report.typeRows || []" :empty-text="'暂无数据'" height="auto">
            <yq-table-column label="请假类型" prop="dimKey" width="130rem">
              <template v-slot="{row}">
                <el-tag size="small" :type="typeTagType(row.dimKey)">{{ typeName(row.dimKey) }}</el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="请假单数" prop="applyCount" align="right" width="110rem">
              <template v-slot="{row}"><span class="num">{{ row.applyCount || 0 }}</span></template>
            </yq-table-column>
            <yq-table-column label="涉及人数" prop="peopleCount" align="right" width="110rem">
              <template v-slot="{row}"><span class="num">{{ row.peopleCount || 0 }}</span></template>
            </yq-table-column>
            <yq-table-column label="请假天数" prop="totalDays" align="right" width="110rem">
              <template v-slot="{row}"><span class="num bold">{{ days(row.totalDays) }}</span></template>
            </yq-table-column>
            <yq-table-column label="单均天数" prop="avgDays" align="right" width="110rem">
              <template v-slot="{row}"><span class="num">{{ days(row.avgDays) }}</span></template>
            </yq-table-column>
            <yq-table-column label="天数占比" prop="daysRatio" min-width="170rem">
              <template v-slot="{row}">
                <div class="ratio-cell">
                <span class="ratio-track">
                  <i :style="{width: (row.daysRatio || 0) + '%', background: '#7a5af8'}"/>
                </span>
                  <span class="ratio-text">{{ row.daysRatio || 0 }}%</span>
                </div>
              </template>
            </yq-table-column>
          </yq-table>
        </list-table>

        <!-- 长期假单（≥ 5 天），详见上方口径说明 -->
        <list-table v-loading="loading" background-color="#FFF" style="height: auto">
          <yq-table :data="report.longLeaveList || []" :empty-text="'暂无长期假单'" height="auto">
            <yq-table-column label="请假单号" prop="leaveNo" width="130rem">
              <template v-slot="{row}"><span class="num muted">{{ row.leaveNo }}</span></template>
            </yq-table-column>
            <yq-table-column label="申请人" prop="applicantName" width="90rem">
              <template v-slot="{row}"><span class="cell-title">{{ row.applicantName || '—' }}</span></template>
            </yq-table-column>
            <yq-table-column label="所属部门" prop="departTitle" width="150rem" :show-overflow-tooltip="true"/>
            <yq-table-column label="请假类型" prop="leaveTypeId" align="center" width="100rem">
              <template v-slot="{row}">
                <el-tag size="small" :type="typeTagType(row.leaveTypeId)">{{ typeName(row.leaveTypeId) }}</el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="起止日期" prop="dateRange" width="200rem">
              <template v-slot="{row}">
                <span class="num">{{ dateOnly(row.startDate) }} ~ {{ dateOnly(row.endDate) }}</span>
              </template>
            </yq-table-column>
            <yq-table-column label="请假天数" prop="leaveDuration" align="right" width="90rem">
              <template v-slot="{row}"><span class="num bold">{{ days(row.leaveDuration) }}</span></template>
            </yq-table-column>
            <yq-table-column label="请假原因" prop="leaveReason" min-width="180rem" :show-overflow-tooltip="true"/>
          </yq-table>
        </list-table>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import DepartSelect from "@/components/h5/data/DepartSelect.vue";
import leaveStatApi from "@/api/dz-crm/leave/leaveStatApi";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";
import YqTable from "@/components/h5/table/YqTable.vue";

const TYPE_COLORS = ["", "warning", "danger", "success", "info"];

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "LeaveStatReport",
  components: {YqTable, ListPage, ListTable, SearchCondition, PageButton, DepartSelect},
  data() {
    return {
      loading: false,
      query: {leaveTypeId: "", departCode: "", monthLabel: ""},
      report: {},
      typeList: [],
      departTree: [],
      dictReady: false,
      dataReady: false,
      typeChart: null,
      departChart: null,
      peopleChart: null,
      monthChart: null,
    };
  },
  beforeRouteLeave(to, from, next) {
    // Util.cachePageData(this, to, from);
    next();
  },
  mounted() {
    // 请假类型一致性字典：假别名称由前端关联，后端只返回 id
    commonApi.queryUniqueDictionaryList("LEAVE_TYPE").then(list => {
      this.typeList = list || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    commonApi.queryDepartList().then(list => this.departTree = list || []);
    this.loadReport();
  },
  unmounted() {
    [this.typeChart, this.departChart, this.peopleChart, this.monthChart].forEach(c => c && c.dispose());
  },
  methods: {
    async loadReport() {
      this.loading = true;
      try {
        const params = {};
        Object.keys(this.query).forEach(k => {
          const v = this.query[k];
          if (v === "" || v === null || v === undefined) {
            return;
          }
          params[k] = v;
        });
        this.report = await leaveStatApi.report(params) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    /** 字典与数据都到位才画，避免假别名渲染成空 */
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      const typeRows = this.report.typeRows || [];
      const departRows = this.report.departRows || [];
      const peopleRows = this.report.peopleRows || [];
      const monthRows = this.report.monthRows || [];

      this.typeChart = this.renderPie(this.typeChart, this.$refs.typeChart,
          typeRows.map(r => ({name: this.typeName(r.dimKey), value: Number(r.totalDays || 0)})));
      this.departChart = this.renderBar(this.departChart, this.$refs.departChart,
          departRows.map(r => r.dimTitle || r.dimKey || "未分配"),
          departRows.map(r => Number(r.totalDays || 0)), "#13b8b8", true);
      // 人员排名：dimTitle 由后端关联职员姓名得到
      this.peopleChart = this.renderBar(this.peopleChart, this.$refs.peopleChart,
          peopleRows.map(r => r.dimTitle || r.dimKey || "—"),
          peopleRows.map(r => Number(r.totalDays || 0)), "#e6a23c", true);
      this.monthChart = this.renderBar(this.monthChart, this.$refs.monthChart,
          monthRows.map(r => r.dimKey), monthRows.map(r => Number(r.totalDays || 0)), "#7a5af8", false);
    },
    renderPie(chart, dom, data) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} 天"},
        legend: {bottom: 0, type: "scroll"},
        series: [{
          type: "pie", radius: ["45%", "68%"], center: ["50%", "45%"],
          label: {formatter: "{b}\n{c} 天"},
          data: data,
        }],
      }, true);
      return chart;
    },
    renderBar(chart, dom, names, data, color, horizontal) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      const categoryAxis = {
        type: "category", data: names,
        axisTick: {alignWithLabel: true}, axisLabel: {color: "#909399", interval: 0},
      };
      const valueAxis = {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}};
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: horizontal ? valueAxis : categoryAxis,
        yAxis: horizontal ? categoryAxis : valueAxis,
        series: [{type: "bar", barWidth: "40%", itemStyle: {color: color}, data: data}],
      }, true);
      return chart;
    },
    /** 假别统计明细的合计行 */
    typeSummary({columns, data}) {
      return columns.map((col, i) => {
        if (i === 0) {
          return "合计";
        }
        if (col.label === "请假单数") {
          return String(data.reduce((s, r) => s + (r.applyCount || 0), 0));
        }
        if (col.label === "涉及人数") {
          return String(this.report.peopleCount || 0);
        }
        if (col.label === "请假天数") {
          return this.days(this.report.passedDays);
        }
        if (col.label === "单均天数") {
          const count = this.report.passedCount || 0;
          return count > 0 ? this.days(Number(this.report.passedDays || 0) / count) : "0";
        }
        if (col.label === "天数占比") {
          return "100%";
        }
        return "";
      });
    },
    typeName(typeId) {
      const t = this.typeList.find(x => x.id === typeId);
      return t ? t.dictName : "";
    },
    typeTagType(typeId) {
      const index = this.typeList.findIndex(x => x.id === typeId);
      if (index < 0) {
        return undefined;
      }
      const color = TYPE_COLORS[index % TYPE_COLORS.length];
      return color === "" ? undefined : color;
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    days(value) {
      if (value === null || value === undefined) {
        return "0";
      }
      return String(Number(value));
    },
  },
};
</script>

<style lang="scss" scoped>
.table-list-margin{
  margin-bottom: 10px;
}
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }

      &.neg {
        color: #e5484d;
      }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;

    .section-sub {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }
  }

  .chart-div {
    height: 280px;
    width: 100%;
  }
}

.ratio-cell {
  display: flex;
  align-items: center;
  gap: 10px;

  .ratio-track {
    flex: 1;
    height: 6px;
    background: #f0f2f5;
    border-radius: 3px;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 3px;
      transition: width .3s;
    }
  }

  .ratio-text {
    flex: 0 0 52px;
    text-align: right;
    color: #606266;
    font-variant-numeric: tabular-nums;
  }
}

.num {
  font-variant-numeric: tabular-nums;
}

.neg {
  color: #e5484d;
}

.bold {
  font-weight: 600;
  color: #303133;
}

.muted {
  color: #909399;
}

.cell-title {
  color: #303133;
  font-weight: 500;
}
</style>
